/**
 * Simulación del picking del WMS (F9), "con un botón".
 *
 * Reemplaza SOLO al WMS (Altanet), que todavía no manda su webhook: la API arma
 * el aviso con las líneas reales de la factura de reserva y lo entrega por el
 * receptor de Altanet de verdad. Desde ahí el middleware llama a Janis de verdad
 * (`start-picking`, `pick`) y, con el picking confirmado, pide el ready-check de
 * la facturación: JEF registra la factura sin inyectar el `ready-for-invoice`.
 *
 * La pantalla solo arma el pedido. La API vuelve a exigir el rol `operator`, que
 * la simulación esté encendida y que `JANIS_DOMAIN` no sea producción; esconder
 * o deshabilitar botones es cortesía, no seguridad.
 *
 * Cada acción genera UN `eventId` y lo conserva: "Reintentar" después de una
 * falla reenvía el mismo, que para la API es un reintento del WMS y nunca un
 * segundo aviso.
 *
 * Venta agrupada (docs/pedidos-de-tienda.md §1.4): la factura de reserva y el
 * DTE son de la venta, y cada pedido hijo tiene su orden de despacho (una por
 * bodega y fecha), su picking y su parte en JEF. La tabla agrupa los hijos bajo
 * su venta, una fila por hijo; los botones son del hijo.
 */
import { useEffect, useId, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import type {
  PickingSimulationLine,
  PickingSimulationOrder,
  SimulatedPickingLineRequest,
  SimulatedPickingMode,
  SimulatedPickingNoticeRequest,
  SimulatedPickingNoticeResponse,
} from '../api/contracts';
import { ApiError } from '../api/errors';
import { useMonitorApi } from '../app/api-context';
import { useCan } from '../auth/auth-context';
import { ErrorBanner } from '../components/ErrorBanner';
import { EmptyState, LoadingBlock } from '../components/Feedback';
import { useAsyncResource } from '../hooks/use-async-resource';
import { formatDateTime } from '../lib/format';

type PickingStatus = NonNullable<PickingSimulationOrder['picking']>['status'];

const PICKING_LABELS: Readonly<Record<PickingStatus, string>> = {
  in_progress: 'en preparación',
  complete: 'completo',
  shortage: 'con faltante',
  cancelled: 'anulado',
  picked: 'confirmado en Janis',
};

const DTE_STATUS_LABELS: Readonly<Record<PickingSimulationOrder['dte']['status'], string>> = {
  issued: 'emitido',
  sap_reserved: 'factura de reserva en SAP',
  custom_data_written: 'customData escrito',
  janis_registered: 'registrado en JEF',
};

const SHARE_STATUS_LABELS: Readonly<Record<PickingSimulationOrder['shareStatus'], string>> = {
  pending: 'sin customData',
  custom_data_written: 'customData escrito',
  janis_registered: 'registrada en JEF',
};

/** Columnas de la tabla: la fila de una venta las abarca todas. */
const COLUMNS = 8;

/**
 * Los pedidos agrupados por venta, en el orden del listado. La clave es la
 * factura de reserva (una por venta: el número de venta solo es único dentro de
 * su canal). Un pedido que no es hijo es su propio grupo.
 */
function groupBySale(orders: readonly PickingSimulationOrder[]): {
  readonly key: string;
  readonly first: PickingSimulationOrder;
  readonly orders: readonly PickingSimulationOrder[];
}[] {
  const groups = new Map<string, PickingSimulationOrder[]>();
  for (const order of orders) {
    const key =
      order.saleNumber === null
        ? `pedido:${order.janisOrderId}`
        : `venta:${order.reference ?? ''}:${String(order.reserveInvoiceDocEntry)}`;
    groups.set(key, [...(groups.get(key) ?? []), order]);
  }
  return [...groups.entries()].flatMap(([key, grouped]) => {
    const [first] = grouped;
    return first === undefined ? [] : [{ key, first, orders: grouped }];
  });
}

const ACK_LABELS: Readonly<Record<SimulatedPickingNoticeResponse['status'], string>> = {
  queued: 'en cola',
  duplicate: 'duplicado',
  quarantined: 'en cuarentena',
};

const MODE_BUTTONS: readonly { readonly mode: SimulatedPickingMode; readonly label: string }[] = [
  { mode: 'progress', label: 'En preparación' },
  { mode: 'complete', label: 'Completo' },
  { mode: 'shortage', label: 'Con faltante' },
];

/** Estados en los que el WMS ya no tiene nada que avisar. */
const CLOSED: ReadonlySet<PickingStatus> = new Set<PickingStatus>(['picked', 'cancelled']);

/**
 * Los modos que todavía tienen sentido para el pedido. Con el WMS ya en
 * completo y el pick pendiente en Janis, «En preparación» y «Con faltante» lo
 * harían retroceder (la API responde 409): solo queda «Completo», que reintenta
 * el pick.
 */
function modeAllowed(order: PickingSimulationOrder, mode: SimulatedPickingMode): boolean {
  const status = order.picking?.status;
  if (status === undefined) {
    return true;
  }
  if (CLOSED.has(status)) {
    return false;
  }
  return status !== 'complete' || mode === 'complete';
}

/** Un id de evento por acción: con él, reintentar es reenviar el mismo aviso. */
function newEventId(): string {
  return `sim-${globalThis.crypto.randomUUID()}`;
}

/** Número de un campo, con coma o punto decimal; `null` si no es número. */
function parseQuantity(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (normalized === '' || !/^\d+(\.\d+)?$/.test(normalized)) {
    return null;
  }
  return Number(normalized);
}

/** La acción abierta en el diálogo: pedido, modo y su `eventId`. */
interface DialogState {
  readonly order: PickingSimulationOrder;
  readonly mode: 'progress' | 'shortage';
  readonly eventId: string;
}

export function PickingSimulationPage(): ReactElement {
  const api = useMonitorApi();
  const canSimulate = useCan('simulation:picking');
  const resource = useAsyncResource(() => api.listPickingSimulationOrders(), [api]);

  const [busyOrder, setBusyOrder] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [failed, setFailed] = useState<{
    readonly request: SimulatedPickingNoticeRequest;
    readonly error: unknown;
  } | null>(null);

  const disabledOff = resource.error instanceof ApiError && resource.error.status === 404;
  const data = resource.data;
  const writable = data?.janisWritesAllowed ?? false;

  const send = (request: SimulatedPickingNoticeRequest): void => {
    setBusyOrder(request.janisOrderId);
    setFailed(null);
    setNotice(null);
    void api.injectPickingNotice(request).then(
      (response) => {
        setBusyOrder(null);
        setDialog(null);
        setNotice(
          `Aviso ${String(response.sequence)} ${ACK_LABELS[response.status]}` +
            (response.redelivery ? ' (reenvío)' : '') +
            ` · pedido ${response.janisOrderId}`,
        );
        resource.reload();
      },
      (error: unknown) => {
        setBusyOrder(null);
        setDialog(null);
        setFailed({ request, error });
      },
    );
  };

  const start = (order: PickingSimulationOrder, mode: SimulatedPickingMode): void => {
    setFailed(null);
    setNotice(null);
    if (mode === 'complete') {
      send({ janisOrderId: order.janisOrderId, mode, eventId: newEventId() });
      return;
    }
    setDialog({ order, mode, eventId: newEventId() });
  };

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Simulación del picking del WMS</h1>
          <p className="page__subtitle">
            Reemplaza solo al WMS (Altanet): el aviso pasa por el receptor real y el middleware
            llama a Janis de verdad (start-picking y pick). Con el pedido completo, F9 pide el
            ready-check de la facturación después de F9_FOLLOW_UP_DELAY_SECONDS y JEF registra la
            factura sin inyectar el ready-for-invoice. Si Janis todavía no era facturable, F9 lo
            vuelve a pedir hasta 5 veces, cada vez más espaciado (unos 20 minutos con la demora por
            defecto). Después, el respaldo es la reconciliación de F9: una vez por intervalo (por
            defecto cada hora) y solo si está encendida.
          </p>
          <p className="page__subtitle">
            En una venta agrupada la factura de reserva y el DTE son de la venta, y el WMS arma una
            orden de despacho por bodega y fecha: cada pedido hijo tiene la suya, su picking y su
            parte en JEF (tipo-folio-n). Si dos hijos comparten bodega y fecha, comparten la orden y
            el aviso trae las líneas de los dos.
          </p>
        </div>
        <div className="row">
          <button type="button" className="button" onClick={resource.reload}>
            Actualizar
          </button>
        </div>
      </header>

      {disabledOff ? (
        <section className="panel">
          <EmptyState title="La simulación está apagada en este ambiente (SIMULATION_ENABLED=false)." />
        </section>
      ) : (
        <>
          {data !== null && !data.pickingEnabled ? (
            <div className="banner banner--warning" role="note">
              F9_PICKING_ENABLED=false: el aviso queda registrado y en cuarentena, sin llegar a
              Janis.
            </div>
          ) : null}
          {data !== null && !data.janisWritesAllowed ? (
            <div className="banner banner--warning" role="note">
              JANIS_DOMAIN es producción o desconocido: la simulación no escribe en Janis.
            </div>
          ) : null}
          {notice === null ? null : (
            <div className="banner banner--info" role="status">
              {notice}
            </div>
          )}
          {failed === null ? null : (
            <ErrorBanner error={failed.error} onRetry={() => send(failed.request)} />
          )}
          <ErrorBanner error={resource.error} onRetry={resource.reload} />

          <section className="panel">
            <header className="panel__header">
              <h2>Pedidos con factura de reserva</h2>
            </header>
            {resource.loading && data === null ? (
              <LoadingBlock />
            ) : data === null ? null : data.orders.length === 0 ? (
              <EmptyState
                title="No hay pedidos esperando picking."
                hint="Aparecen cuando la facturación deja la factura de reserva en SAP."
              />
            ) : (
              <div className="table__wrap">
                <table className="table table--stack">
                  <thead>
                    <tr>
                      <th scope="col">Pedido de Janis</th>
                      <th scope="col">DTE</th>
                      <th scope="col">Factura de reserva</th>
                      <th scope="col">Orden de despacho</th>
                      <th scope="col">Picking</th>
                      <th scope="col">Secuencia</th>
                      <th scope="col">En Janis</th>
                      <th scope="col">Simular el WMS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupBySale(data.orders).map((group) => (
                      <Fragment key={group.key}>
                        {group.first.saleNumber === null ? null : (
                          <SaleRow first={group.first} count={group.orders.length} />
                        )}
                        {group.orders.map((order) => (
                          <OrderRow
                            key={order.janisOrderId}
                            order={order}
                            disabled={!canSimulate || !writable || busyOrder !== null}
                            busy={busyOrder === order.janisOrderId}
                            onStart={(mode) => start(order, mode)}
                          />
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {dialog === null ? null : (
        <LinesDialog
          state={dialog}
          busy={busyOrder === dialog.order.janisOrderId}
          onCancel={() => setDialog(null)}
          onConfirm={(lines) =>
            send({
              janisOrderId: dialog.order.janisOrderId,
              mode: dialog.mode,
              eventId: dialog.eventId,
              ...(lines.length === 0 ? {} : { lines }),
            })
          }
        />
      )}
    </div>
  );
}

/** La fila de una venta agrupada: lo que comparten sus hijos. */
function SaleRow({
  first,
  count,
}: {
  readonly first: PickingSimulationOrder;
  readonly count: number;
}): ReactElement {
  return (
    <tr className="table__group">
      <td colSpan={COLUMNS}>
        <strong>Venta {first.saleNumber}</strong>
        <span className="muted">
          {' · '}
          {count === 1 ? '1 pedido' : `${String(count)} pedidos`} · DTE{' '}
          <span className="mono">{`${String(first.dte.documentType)}-${String(first.dte.folio)}`}</span>{' '}
          ({DTE_STATUS_LABELS[first.dte.status]}) · factura de reserva{' '}
          <span className="mono">{String(first.reserveInvoiceDocEntry)}</span>
        </span>
      </td>
    </tr>
  );
}

function OrderRow({
  order,
  disabled,
  busy,
  onStart,
}: {
  readonly order: PickingSimulationOrder;
  readonly disabled: boolean;
  readonly busy: boolean;
  readonly onStart: (mode: SimulatedPickingMode) => void;
}): ReactElement {
  const picking = order.picking;
  return (
    <tr>
      <td className="mono table__lead nowrap">
        <Link to={`/traza/${encodeURIComponent(order.janisOrderId)}`}>{order.janisOrderId}</Link>
        {order.commerceId === null ? null : <div className="muted">{order.commerceId}</div>}
      </td>
      <td data-label="DTE">
        {order.saleNumber === null ? (
          <>
            <span className="mono">{`${String(order.dte.documentType)}-${String(order.dte.folio)}`}</span>
            <span className="muted"> · {DTE_STATUS_LABELS[order.dte.status]}</span>
          </>
        ) : (
          // Un hijo registra SU parte en JEF: el estado es el de la parte, no el del DTE.
          <>
            <span className="mono">{order.jefNumber}</span>
            <span className="muted"> · {SHARE_STATUS_LABELS[order.shareStatus]}</span>
          </>
        )}
      </td>
      <td className="mono nowrap" data-label="Factura de reserva">
        {String(order.reserveInvoiceDocEntry)}
      </td>
      <td data-label="Orden de despacho">
        {order.wmsDocument === null ? (
          <span className="muted">—</span>
        ) : (
          <span className="mono">{order.wmsDocument}</span>
        )}
        {order.warehouse === null && order.shipDate === null ? null : (
          <div className="muted">
            {[order.warehouse, order.shipDate === null ? null : `picking ${order.shipDate}`]
              .filter((parte) => parte !== null)
              .join(' · ')}
          </div>
        )}
      </td>
      <td className="nowrap table__pill" data-label="Picking">
        <span className={picking?.status === 'picked' ? 'badge badge--ok' : 'badge'}>
          {picking === null ? 'sin avisos' : PICKING_LABELS[picking.status]}
        </span>
      </td>
      <td className="mono nowrap" data-label="Secuencia">
        {picking === null ? '—' : String(picking.lastSequence)}
      </td>
      <td data-label="En Janis">
        {picking === null ? (
          '—'
        ) : (
          <ul className="stack muted">
            <li>start-picking: {formatDateTime(picking.janisStartPickingAt)}</li>
            <li>pick: {formatDateTime(picking.janisPickedAt)}</li>
            <li>
              ready-check pedido: {formatDateTime(picking.readyCheckRequestedAt)}
              {picking.readyCheckRequestedAt === null ? null : (
                // La lista solo trae pedidos sin la factura en JEF: pedirlo no
                // es haberlo registrado. F9 lo vuelve a pedir en cada revisión.
                <span> · la factura todavía no está en JEF</span>
              )}
            </li>
          </ul>
        )}
      </td>
      <td>
        <div className="table__actions">
          {MODE_BUTTONS.map(({ mode, label }) => (
            <button
              key={mode}
              type="button"
              className={
                mode === 'complete' ? 'button button--sm button--primary' : 'button button--sm'
              }
              disabled={disabled || !modeAllowed(order, mode)}
              aria-label={`${label} · pedido ${order.janisOrderId}`}
              onClick={() => onStart(mode)}
            >
              {busy && mode === 'complete' ? 'Enviando…' : label}
            </button>
          ))}
        </div>
      </td>
    </tr>
  );
}

/** Lo que se edita de una línea en el diálogo. */
interface LineDraft {
  readonly value: string;
  readonly short: boolean;
  readonly reason: string;
}

/**
 * En preparación y Con faltante: lo pickeado ACUMULADO de cada línea. Nunca
 * baja de lo ya aplicado ni pasa de lo pedido. En preparación manda solo las
 * líneas que cambiaron; con faltante, las marcadas como cortas.
 */
function LinesDialog({
  state,
  busy,
  onCancel,
  onConfirm,
}: {
  readonly state: DialogState;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: (lines: SimulatedPickingLineRequest[]) => void;
}): ReactElement {
  const titleId = useId();
  const shortage = state.mode === 'shortage';
  const [drafts, setDrafts] = useState<Readonly<Record<number, LineDraft>>>(() =>
    Object.fromEntries(
      state.order.lines.map((line) => [
        line.baseLine,
        { value: String(line.picked), short: false, reason: '' },
      ]),
    ),
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  const update = (baseLine: number, change: Partial<LineDraft>): void => {
    setDrafts((previous) => {
      const current = previous[baseLine] ?? { value: '0', short: false, reason: '' };
      return { ...previous, [baseLine]: { ...current, ...change } };
    });
  };

  const problemFor = (line: PickingSimulationLine): string | null => {
    const draft = drafts[line.baseLine];
    if (draft === undefined || (shortage && !draft.short)) {
      return null;
    }
    const picked = parseQuantity(draft.value);
    if (picked === null) {
      return 'Escribe un número.';
    }
    if (picked < line.picked) {
      return `No puede bajar de lo ya pickeado (${String(line.picked)}).`;
    }
    if (shortage ? picked >= line.requested : picked > line.requested) {
      return shortage
        ? `Un faltante pickea menos de lo pedido (${String(line.requested)}).`
        : `No puede pasar de lo pedido (${String(line.requested)}).`;
    }
    return null;
  };

  const problems = state.order.lines.map(problemFor);
  const marked = state.order.lines.filter((line) => drafts[line.baseLine]?.short === true);
  const valid = problems.every((problem) => problem === null) && (!shortage || marked.length > 0);

  const collect = (): SimulatedPickingLineRequest[] =>
    state.order.lines.flatMap((line): SimulatedPickingLineRequest[] => {
      const draft = drafts[line.baseLine];
      const picked = draft === undefined ? null : parseQuantity(draft.value);
      if (draft === undefined || picked === null) {
        return [];
      }
      if (shortage) {
        if (!draft.short) {
          return [];
        }
        const reason = draft.reason.trim();
        return [{ baseLine: line.baseLine, picked, ...(reason === '' ? {} : { reason }) }];
      }
      return picked === line.picked ? [] : [{ baseLine: line.baseLine, picked }];
    });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (valid && !busy) {
      onConfirm(collect());
    }
  };

  return (
    <div className="dialog__backdrop">
      <form
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={submit}
      >
        <h2 id={titleId}>
          {shortage ? 'Con faltante' : 'En preparación'} · pedido {state.order.janisOrderId}
        </h2>
        <p className="muted">
          {shortage
            ? 'Marca las líneas que quedan cortas y cuánto se alcanzó a pickear. Las demás se pickean completas.'
            : 'Lo pickeado ACUMULADO de cada línea, en unidades de SAP. Las que no cambies conservan lo ya aplicado.'}
        </p>
        <div className="table__wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Línea</th>
                <th scope="col">SKU</th>
                <th scope="col">Solicitada</th>
                {shortage ? <th scope="col">Falta</th> : null}
                <th scope="col">Pickeado acumulado</th>
                {shortage ? <th scope="col">Motivo</th> : null}
              </tr>
            </thead>
            <tbody>
              {state.order.lines.map((line, index) => {
                const draft = drafts[line.baseLine];
                const problem = problems[index] ?? null;
                const editable = !shortage || draft?.short === true;
                return (
                  <tr key={line.baseLine}>
                    <td className="mono">{String(line.baseLine)}</td>
                    <td className="mono">{line.sku}</td>
                    <td className="mono">{String(line.requested)}</td>
                    {shortage ? (
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Falta ${line.sku}`}
                          checked={draft?.short ?? false}
                          onChange={(event) =>
                            update(line.baseLine, { short: event.target.checked })
                          }
                        />
                      </td>
                    ) : null}
                    <td>
                      <input
                        className="input"
                        type="number"
                        min={line.picked}
                        max={line.requested}
                        step="any"
                        aria-label={`Pickeado acumulado de ${line.sku}`}
                        aria-invalid={problem !== null}
                        disabled={!editable}
                        value={editable ? (draft?.value ?? '') : String(line.requested)}
                        onChange={(event) => update(line.baseLine, { value: event.target.value })}
                      />
                      {problem === null ? null : <div className="muted">{problem}</div>}
                    </td>
                    {shortage ? (
                      <td>
                        <input
                          className="input"
                          aria-label={`Motivo del faltante de ${line.sku}`}
                          disabled={draft?.short !== true}
                          value={draft?.reason ?? ''}
                          placeholder="Simulacion: sin stock en la ubicacion"
                          onChange={(event) =>
                            update(line.baseLine, { reason: event.target.value })
                          }
                        />
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="dialog__actions">
          <button type="button" className="button" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className="button button--primary" disabled={busy || !valid}>
            {busy ? 'Enviando…' : 'Entregar aviso'}
          </button>
        </div>
      </form>
    </div>
  );
}
